<template>
  <div class="rounded-lg p-4 border border-border bg-surface">
    <div class="flex items-start justify-between mb-3">
      <div class="min-w-0 flex-1">
        <div class="text-xs font-medium uppercase tracking-wider mb-1 text-text-muted">
          {{ block.label }}
        </div>
        <div class="flex items-center gap-2">
          <span :class="['text-3xl font-bold leading-none', otsStatusClass(block)]">
            {{ block.pct != null ? formatOtsPct(block.pct) + '%' : 'N/A' }}
          </span>
          <OtsTrendDelta :delta="block.delta" :vs-date="block.deltaVsDate" size="lg" class="-mt-0.5" />
        </div>
      </div>
      <div
        :class="['flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center', otsBadgeBgClass(block)]"
        aria-hidden="true"
      >
        <component
          v-if="getOtsStatus(block) === 'good'"
          :is="Rocket"
          :class="['w-5 h-5', otsBadgeIconClass(block)]"
        />
        <i v-else :class="[otsStatusIconRemix(block), 'text-xl', otsBadgeIconClass(block)]" aria-hidden="true" />
      </div>
    </div>
    <div class="flex flex-nowrap items-center gap-x-3 text-sm whitespace-nowrap">
      <div class="flex items-center gap-1.5">
        <span class="text-text-muted">Shipped:</span>
        <span class="font-semibold text-text">{{ formatOtsInt(block.shipped) }}</span>
      </div>
      <span class="text-text-muted" aria-hidden="true">|</span>
      <div class="flex items-center gap-1.5">
        <span class="text-text-muted">On Time:</span>
        <span :class="['font-semibold', otsStatusClass(block)]">{{ formatOtsInt(block.onTime) }}</span>
      </div>
      <template v-if="showLead && block.leadDays != null">
        <span class="text-text-muted" aria-hidden="true">|</span>
        <div class="flex items-center gap-1.5">
          <span class="text-text-muted">Lead:</span>
          <span class="font-semibold text-text">{{ formatOtsLeadDays(block.leadDays) }}</span>
        </div>
      </template>
    </div>
    <div
      v-if="valueFormatted != null && valueFormatted !== ''"
      class="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm"
    >
      <div class="flex items-center gap-2">
        <span class="text-text-muted">Value:</span>
        <span class="font-semibold text-text">{{ valueFormatted }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Rocket } from 'lucide-vue-next'
import OtsTrendDelta from '@/components/operations/OtsTrendDelta.vue'
import {
  formatOtsInt,
  formatOtsLeadDays,
  formatOtsPct,
  getOtsStatus,
  otsBadgeBgClass,
  otsBadgeIconClass,
  otsStatusClass,
  otsStatusIconRemix,
  type OtsKpiBlock
} from '@/utils/otsKpiPresentation'

withDefaults(
  defineProps<{
    block: OtsKpiBlock
    /** Pre-formatted shipped-sale string (e.g. from formatCurrencyFromCents). Omit to hide the Value row. */
    valueFormatted?: string | null
    /** Show the avg lead-time row. Off for the OTS page KPI bar; on (default) for the nav KPI popover. */
    showLead?: boolean
  }>(),
  {
    valueFormatted: undefined,
    showLead: true
  }
)
</script>
